import type { ReactNode } from "preact/compat";
import { Navigate, useLocation } from "react-router";
import {
	isLoadingAuthenticationResult,
	isSignedInAuthenticationResult,
	isSignedOutAuthenticationResult,
	useAuthenticationState,
} from "~/hooks/auth-state.tsx";
import LoadingSpinner from "~/components/common/loading-spinner.tsx";

type AuthRouteGuardProps = {
	readonly children: ReactNode;
};

// Auth has three states, so each guard needs three branches. Folding "loading"
// into the redirect branch is what previously let these two redirect into each
// other until the tab ran out of memory.
function RequireSignedIn({ children }: AuthRouteGuardProps) {
	const authResult = useAuthenticationState();
	const { pathname } = useLocation();

	if (isLoadingAuthenticationResult(authResult)) {
		return <LoadingSpinner />;
	}
	if (!isSignedInAuthenticationResult(authResult)) {
		const searchParams = new URLSearchParams({ redirectTo: pathname });
		return <Navigate to={`/login/?${searchParams}`} replace />;
	}
	return <>{children}</>;
}

function RequireSignedOut({ children }: AuthRouteGuardProps) {
	const authResult = useAuthenticationState();

	if (isLoadingAuthenticationResult(authResult)) {
		return <LoadingSpinner />;
	}
	if (!isSignedOutAuthenticationResult(authResult)) {
		return <Navigate to="/profile/" replace />;
	}
	return <>{children}</>;
}

export { RequireSignedIn, RequireSignedOut };
